<template>
  <div class="DateCarousel">
    <IconButton
      icon="chevron-left"
      size="lg"
      class="DateCarousel-navIcon"
      :ariaLabel="t('ui.components.dateCarousel.previousDates')"
      :disabled="!canNavigatePrevious"
      @click="navigatePrevious"
    />

    <div class="DateCarousel-chips">
      <DateChip
        v-for="{ date, isSelected, isDisabled, id, subtitle } of dateItems"
        :key="id"
        :date="date"
        :selected="isSelected"
        :subtitle="subtitle"
        :disabled="isDisabled"
        @click="handleDateClick"
      />
    </div>

    <IconButton
      icon="chevron-right"
      size="lg"
      class="DateCarousel-navIcon"
      :ariaLabel="t('ui.components.dateCarousel.nextDates')"
      :disabled="!canNavigateNext"
      @click="navigateNext"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { isEqual, startOfToday, isBefore, startOfDay, addDays, subDays, isAfter, isSameDay } from 'date-fns';
import { whenever } from '@vueuse/core';
import { useResponsive } from '@cvo/ui/composables';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { getIsoDate } from '@cvo/domain/utils/dateUtils';
import IconButton from '../../atoms/IconButton/IconButton.vue';
import DateChip from './DateChip.vue';
import { type DateCarouselProps, type DateId, type NavigationDirection, BUFFER_SIDES, VISIBLE_COUNT_BY_BREAKPOINT } from './DateCarousel.model';

const props = withDefaults(defineProps<DateCarouselProps>(), {
  defaultDaysCount: 14,
  disabledDates: () => [],
});

const emit = defineEmits<{
  'date-select': [date: Date];
  navigate: [direction: NavigationDirection];
}>();

const NO_AVAILABILITIES_COUNT_PLACEHOLDER = '-';

const { t } = useI18n();
const { currentBreakpoint } = useResponsive();

const selectedDate = defineModel<Date>();

/** Index of the leftmost day visible on the carousel */
const windowStart = ref(0);
const today = startOfToday();

const availabilityCountByDay = computed(() => new Map<DateId, number>(props.availabilities?.map(({ date, count }) => [date, count])));
const isShowingAvailabilities = computed(() => props.availabilities !== undefined);
const disabledDateIds = computed(() => new Set<DateId>(props.disabledDates.map(getDateId)));

const visibleDatesCount = computed(() => VISIBLE_COUNT_BY_BREAKPOINT[currentBreakpoint.value]);
/** Half the visible count — number of buffer dates added before/after the booking window. */
const bufferDays = computed(() => Math.floor(visibleDatesCount.value / BUFFER_SIDES));

const defaultDates = computed(() => {
  const startDate = subDays(today, bufferDays.value);
  const totalDays = props.defaultDaysCount + bufferDays.value * BUFFER_SIDES; // buffer on both sides
  return Array.from({ length: totalDays }, (__, index) => addDays(startDate, index));
});

const effectiveDates = computed(() => props.dates || defaultDates.value);

const visibleDates = computed(() => {
  const start = windowStart.value;
  const end = start + visibleDatesCount.value;
  return effectiveDates.value.slice(start, end);
});

const dateItems = computed(() => {
  return visibleDates.value.map((date) => {
    const id = getDateId(date);
    const availabilityCount = availabilityCountByDay.value.get(id);
    const subtitle = availabilityCount !== undefined
      ? t('ui.components.dateCarousel.nbAvailabilities', { count: availabilityCount }, availabilityCount)
      : isShowingAvailabilities.value ? NO_AVAILABILITIES_COUNT_PLACEHOLDER : undefined;

    return {
      date,
      id,
      isSelected: isDateSelected(date),
      isDisabled: isDateDisabled(date) || props.disabled,
      subtitle,
    };
  });
});

const canNavigatePrevious = computed(() => !props.disabled && (windowStart.value > 0));

const canNavigateNext = computed(() => {
  return !props.disabled && (windowStart.value + visibleDatesCount.value < effectiveDates.value.length);
});

function getDateId (date: Date): DateId {
  return getIsoDate(date);
};

function isDateSelected (date: Date): boolean {
  return selectedDate.value ? isSameDay(date, selectedDate.value) : false;
};

function isOutsideBookingWindow (normalizedDate: Date): boolean {
  const isBeforeMinDate = props.minDate ? isBefore(normalizedDate, startOfDay(props.minDate)) : false;

  const isAfterMaxDate = props.maxDate
    ? isAfter(normalizedDate, startOfDay(props.maxDate))
    : isAfter(normalizedDate, addDays(today, props.defaultDaysCount - 1));

  return isBeforeMinDate || isAfterMaxDate;
};

function isDateDisabled (date: Date): boolean {
  const normalizedDate = startOfDay(date);
  const id = getDateId(normalizedDate);

  const isPastDate = isBefore(normalizedDate, today);
  const isExplicitlyDisabled = disabledDateIds.value.has(id);
  // A date present in availabilities with a count of 0 is unavailable
  const isUnavailable = availabilityCountByDay.value.get(id) === 0;

  return isPastDate || isOutsideBookingWindow(normalizedDate) || isExplicitlyDisabled || isUnavailable;
};

function handleDateClick (date: Date) {
  selectDate(date);
};

function selectDate (date: Date) {
  if (isDateDisabled(date)) return;

  selectedDate.value = date;
  emit('date-select', date);
};

function navigatePrevious () {
  if (canNavigatePrevious.value) {
    windowStart.value = Math.max(0, windowStart.value - 1);
    emit('navigate', 'previous');
  }
};

function navigateNext () {
  if (canNavigateNext.value) {
    const maxStart = effectiveDates.value.length - visibleDatesCount.value;
    windowStart.value = Math.min(maxStart, windowStart.value + 1);
    emit('navigate', 'next');
  }
};

// Auto-center selected date when it changes
whenever(
  selectedDate,
  (newSelectedDate) => {
    const selectedIndex = effectiveDates.value.findIndex((date) => isEqual(startOfDay(date), startOfDay(newSelectedDate)));
    if (selectedIndex === -1) return;

    const idealStart = Math.max(0, selectedIndex - bufferDays.value);
    const maxStart = Math.max(0, effectiveDates.value.length - visibleDatesCount.value);

    windowStart.value = Math.min(idealStart, maxStart);
  },
  { immediate: true },
);
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .DateCarousel {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: nowrap;
    gap: vars.$dimensions-d-6;
    background-color: vars.$organisms-carousel-date-bg-color;
    border: vars.$organisms-carousel-date-border-stroke solid vars.$organisms-carousel-date-border-color;
    padding: vars.$dimensions-d-3 0;
    border-radius: vars.$organisms-carousel-date-lg-md-border-radius;

    @include responsive.mobile {
      gap: vars.$dimensions-d-2;
      border-radius: vars.$organisms-carousel-date-sm-border-radius;
    }

    &-navIcon {
      border-radius: vars.$border-radius-full;
      background-color: vars.$organisms-carousel-date-icon-bg-color;
      color: vars.$organisms-carousel-date-icon-enabled-color;
      padding: vars.$dimensions-d-2;
      &.is-disabled {
        color: vars.$organisms-carousel-date-icon-disabled-color;
      }
    }

    &-chips {
      display: flex;
      gap: vars.$dimensions-d-3;
    }
  }
}
</style>
